import React, { Component } from "react";
import PropTypes from "prop-types";
import queryString from "query-string";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import { withStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import { FilterList, CloudOff, Cloud } from "@material-ui/icons";
import Badge from "@material-ui/core/Badge";
import Tooltip from "@material-ui/core/Tooltip";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import EyeIcon from "@material-ui/icons/RemoveRedEye";
import dateformat from "dateformat";
import { withRouter } from "react-router-dom";
import Pager from "../Pager/Pager";
import SketchOrderStatusChip from "../SketchOrderStatusChip/SketchOrderStatusChip";
import TimeSinceRequestedChip from "../TimeSinceRequestedChip/TimeSinceRequestedChip";
import ChoseSketchOrdersFilterDialog from "../ChooseSketchOrderFilterDialog";
import StatusSketchOrderFilterDialog from "../StatusSketchOrderFilterDialog";
import OrganizationSketchOrderFilterDialog from "../../containers/OrganizationSketchOrderFilterDialog";
import RequesterSketchOrderFilterDialog from "../../containers/RequesterSketchOrderFilterDialog";
import OwnerSketchOrderFilterDialog from "../../containers/OwnerSketchOrderFilterDialog";
import { SketchOrderSortParameter } from "../../lib/sketchOrderSortParameters";
import { OrderParameter } from "../../lib/OrderParameters";

const style = (theme) => ({
  flex: {
    flexGrow: 1,
  },
  subtitleIcon: {
    display: "inline",
    marginRight: theme.spacing(1),
  },
  subtitle: {
    display: "inline",
    verticalAlign: "super",
  },
  title: {
    display: "inline",
    marginRight: 8,
  },
  tableWrapper: {
    overflowX: "auto",
  },
  margin: {
    marginRight: theme.spacing(1),
  },
});

const blueRoofOrgId = parseInt(process.env.REACT_APP_BLUE_ROOF_ORG_ID);

class SketchOrderTable extends Component {
  state = {
    chooseFilterDialogOpen: false,
    statusFilterDialogOpen: false,
    organizationFilterDialogOpen: false,
    requesterFilterDialogOpen: false,
    ownerFilterDialogOpen: false,
  };

  // Choose Filter Dialog

  openFilterSketchOrderDialog = () => {
    this.setState({ chooseFilterDialogOpen: true });
  };

  closeFilterSketchOrderDialog = () => {
    this.setState({ chooseFilterDialogOpen: false });
  };

  applyFilter = (name, value, clearFilters = false) => {
    let newQueryString;
    if (clearFilters) {
      const newQueryParam = {
        [name]: value,
      };
      newQueryString = queryString.stringify(newQueryParam);
    } else {
      const queryParams = queryString.parse(this.props.location.search);
      const newQueryParams = Object.assign({}, queryParams, {
        [name]: value,
      });

      newQueryString = queryString.stringify(newQueryParams);
    }
    this.props.history.push(
      `${this.props.location.pathname}?${newQueryString}`
    );
  };

  // Status Filter

  openStatusFilterDialog = () => {
    this.setState({
      chooseFilterDialogOpen: false,
      statusFilterDialogOpen: true,
    });
  };

  applyStatusFilters = (statuses) => {
    const statusIds = statuses.map((status) => {
      return status.id;
    });

    this.applyFilter("statusIds", statusIds);

    this.closeStatusFilterDialog();
  };

  closeStatusFilterDialog = () => {
    this.setState({
      chooseFilterDialogOpen: true,
      statusFilterDialogOpen: false,
    });
  };

  // Organization Filter

  openOrganizationFilterDialog = () => {
    this.setState({
      chooseFilterDialogOpen: false,
      organizationFilterDialogOpen: true,
    });
  };

  applyOrganizationFilter = (organizationId) => {
    this.applyFilter("organizationId", organizationId);

    this.closeOrganizationFilterDialog();
  };

  closeOrganizationFilterDialog = () => {
    this.setState({
      chooseFilterDialogOpen: true,
      organizationFilterDialogOpen: false,
    });
  };

  // Requester Filter

  openRequesterFilterDialog = () => {
    this.setState({
      chooseFilterDialogOpen: false,
      requesterFilterDialogOpen: true,
    });
  };

  applyRequesterFilter = (userId) => {
    this.applyFilter("requesterUserId", userId, true);

    this.closeRequesterFilterDialog();
  };

  closeRequesterFilterDialog = () => {
    this.setState({
      chooseFilterDialogOpen: true,
      requesterFilterDialogOpen: false,
    });
  };

  // Owner Filter

  openOwnerFilterDialog = () => {
    this.setState({
      chooseFilterDialogOpen: false,
      ownerFilterDialogOpen: true,
    });
  };

  applyOwnerFilter = (userId) => {
    this.applyFilter("ownerUserId", userId, true);

    this.closeOwnerFilterDialog();
  };

  closeOwnerFilterDialog = () => {
    this.setState({
      chooseFilterDialogOpen: true,
      ownerFilterDialogOpen: false,
    });
  };

  sort = (newSortParameter) => {
    let order;

    // If are changing the sort parameter, the order should be descending
    // Otherwise, toggle what the order this is
    if (this.props.sort !== newSortParameter) {
      order = OrderParameter.Descending;
    } else {
      switch (this.props.order) {
        case OrderParameter.Descending:
          order = OrderParameter.Ascending;
          break;
        case OrderParameter.Ascending:
          order = OrderParameter.Descending;
          break;
        default:
          break;
      }
    }

    this.props.onSortClick(newSortParameter, order);
  };

  sortByRequestedDate = () => {
    // None sort parameter is the default parameter.
    // The default sort is by requestedAt
    this.sort(SketchOrderSortParameter.None);
  };

  sortByUpdatedDate = () => {
    this.sort(SketchOrderSortParameter.UpdatedAt);
  };

  sortByTimeSinceRequested = () => {
    this.sort(SketchOrderSortParameter.TimeSinceRequested);
  };

  sortByDueDate = () => {
    this.sort(SketchOrderSortParameter.DueDate);
  };

  renderTitle = () => {
    if (this.props.totalOrders === 1)
      return `${this.props.totalOrders} Sketch Order`;

    return `${this.props.totalOrders} Sketch Orders`;
  };

  renderSketchOrderStatusChip = (order) => {
    const { hasNearmapCoverage, sketchOrderStatus, rushed } = order;
    const chip = <SketchOrderStatusChip status={sketchOrderStatus} />;

    if (hasNearmapCoverage && !rushed) {
      return (
        <Badge badgeContent="HD" color="primary">
          {chip}
        </Badge>
      );
    }

    if (hasNearmapCoverage && rushed) {
      return (
        <Badge badgeContent="HD" color="primary">
          <Badge
            badgeContent="RUSH"
            color="error"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            {chip}
          </Badge>
        </Badge>
      );
    }

    if (rushed) {
      return (
        <Badge
          badgeContent="RUSH"
          color="error"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          {chip}
        </Badge>
      );
    }
    return chip;
  };

  renderProjectName = (order) => {
    if (!order.projectId) {
      return <Typography>{order.projectName}</Typography>;
    }

    const impersonatorUserName = this.props.currentUserName.split("@")[0];
    return (
      <a
        href={`${
          process.env.REACT_APP_DECEPTIVE_DOLPHIN_HOST
        }login?returnurl=/projects/${
          order.projectId
        }&username=${encodeURIComponent(
          `${order.owner.userName}^${impersonatorUserName}`
        )}`}
        target="_blank"
      >
        {order.projectName}
      </a>
    );
  };

  renderSketchOrder = (id) => {
    const order = this.props.sketchOrders[id];
    return (
      <TableRow
        key={order.id}
        hover
        onDoubleClick={() => this.props.history.push(`/orders/${order.id}`)}
      >
        {this.props.showStatus && (
          <TableCell>{this.renderSketchOrderStatusChip(order)}</TableCell>
        )}
        <TableCell>{order.sketchReportType}</TableCell>
        {this.props.showRequestedAtTime && (
          <TableCell>
            {order.requestedDate
              ? dateformat(order.requestedDate, "m/d/yyyy h:MM TT")
              : ""}
          </TableCell>
        )}

        {this.props.showLastUpdatedAtTime && (
          <TableCell>
            {order.updatedAt
              ? dateformat(order.updatedAt, "m/d/yyyy h:MM TT")
              : ""}
          </TableCell>
        )}
        {this.props.showDueDate && (
          <TableCell>
            {order.dueDate
              ? dateformat(order.dueDate, "m/d/yyyy h:MM TT")
              : "None"}
          </TableCell>
        )}

        {this.props.showTimeSinceRequested && (
          <TableCell>
            <TimeSinceRequestedChip
              value={order.timeSinceRequested}
              isBlueRoof={order.organizationId === blueRoofOrgId}
            />
          </TableCell>
        )}
        {this.props.showOrganizationName && (
          <TableCell>
            {this.props.showLinksToSupportDashboard &&
            process.env.REACT_APP_ENVIRONMENT.endsWith("production") ? (
              <a
                href={`${process.env.REACT_APP_SUPPORT_DASHBOARD_HOST}organizations/${order.organization.id}/show`}
                target="_blank"
              >
                {order.organization.name}
              </a>
            ) : (
              order.organization.name
            )}
          </TableCell>
        )}
        {this.props.showSketchTech && (
          <TableCell>
            {order.sketchTech && order.sketchTech.userName
              ? order.sketchTech.userName
              : ""}
          </TableCell>
        )}
        {this.props.showRequester && (
          <TableCell>
            {this.props.showLinksToSupportDashboard &&
            process.env.REACT_APP_ENVIRONMENT.endsWith("production") ? (
              <a
                href={`${process.env.REACT_APP_SUPPORT_DASHBOARD_HOST}userprofiles/${order.requester.userId}/show`}
                target="_blank"
              >
                {order.requester.userName}
              </a>
            ) : (
              order.requester.userName
            )}
          </TableCell>
        )}
        {this.props.showAddress && <TableCell>{order.address}</TableCell>}
        {this.props.showProjectName && (
          <TableCell>{this.renderProjectName(order)}</TableCell>
        )}
        {this.props.showCompletedDate && (
          <TableCell>
            {order.completedDate
              ? dateformat(order.completedDate, "m/d/yyyy h:MM TT")
              : ""}
          </TableCell>
        )}
        {this.props.showReviewer && (
          <TableCell>
            {order.reviewer && (
              <div>
                {this.props.showLinksToSupportDashboard &&
                process.env.REACT_APP_ENVIRONMENT.endsWith("production") ? (
                  <a
                    href={`${process.env.REACT_APP_SUPPORT_DASHBOARD_HOST}userprofiles/${order.reviewer.userId}/show`}
                    target="_blank"
                  >
                    {order.reviewer.userName}
                  </a>
                ) : (
                  order.reviewer.userName
                )}
              </div>
            )}
          </TableCell>
        )}
        {this.props.showCost && (
          <TableCell>
            {order.cost.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </TableCell>
        )}
        <TableCell>
          <Button
            onClick={() => this.props.history.push(`/orders/${order.id}`)}
            color="primary"
          >
            <EyeIcon className={this.props.classes.margin} />
            {order.id}
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  determineNumberOfFilersBeingApplied = () => {
    if (this.props.numberOfRequesterFilters) {
      return this.props.numberOfRequesterFilters;
    }

    return (
      this.props.numberOfStatusFilters + this.props.numberOfOrganizationFilters
    );
  };

  determineIfOrderIsAscOrDesc = () => {
    switch (this.props.order) {
      case OrderParameter.Ascending:
        return "asc";
      case OrderParameter.Descending:
        return "desc";
      default:
        break;
    }
  };

  render() {
    const numberOfFiltersApplied = this.determineNumberOfFilersBeingApplied();
    return (
      <div>
        <Paper>
          <Toolbar>
            <div className={this.props.classes.flex}>
              <div className={this.props.classes.flex}>
                <Typography
                  variant="subtitle1"
                  className={this.props.classes.subtitleIcon}
                >
                  {this.props.subtitleIcon}
                </Typography>
                <Typography
                  variant="subtitle1"
                  className={this.props.classes.subtitle}
                >
                  {this.props.subtitle}
                </Typography>
              </div>
              <Typography variant="h6" className={this.props.classes.title}>
                {this.renderTitle()}
              </Typography>
              {this.props.isFetching && <CircularProgress />}
            </div>
            {this.props.showStormFilter && (
              <IconButton onClick={this.props.filterStormClick}>
                {this.props.filterStorm ? <CloudOff /> : <Cloud />}
              </IconButton>
            )}
            {this.props.canFilter && numberOfFiltersApplied === 0 && (
              <IconButton onClick={this.openFilterSketchOrderDialog}>
                <FilterList />
              </IconButton>
            )}
            {this.props.canFilter && numberOfFiltersApplied > 0 && (
              <IconButton onClick={this.openFilterSketchOrderDialog}>
                <Badge badgeContent={numberOfFiltersApplied} color="secondary">
                  <FilterList />
                </Badge>
              </IconButton>
            )}
            <Pager
              currentPage={this.props.currentPage}
              totalPages={this.props.totalPages}
              nextPageClick={this.props.onNextPageClick}
              prevPageClick={this.props.onPrevPageClick}
            />
          </Toolbar>
          <div className={this.props.classes.tableWrapper}>
            <Table>
              <TableHead>
                <TableRow>
                  {this.props.showStatus && <TableCell>Status</TableCell>}
                  {this.props.showRequestedAtTime && (
                    <TableCell>
                      {this.props.canSortByRequestedDate && (
                        <Tooltip title="Sort">
                          <TableSortLabel
                            active={
                              this.props.sort === SketchOrderSortParameter.None
                            }
                            onClick={this.sortByRequestedDate}
                            direction={this.determineIfOrderIsAscOrDesc()}
                          >
                            Requested
                          </TableSortLabel>
                        </Tooltip>
                      )}
                      {!this.props.canSortByRequestedDate && (
                        <div>Requested</div>
                      )}
                    </TableCell>
                  )}
                  {/* TODO: dhoward -- this is the new headder */}
                  <TableCell>Report Type</TableCell>
                  {/* TODO: dhoward -- this is the new headder */}
                  {this.props.showLastUpdatedAtTime && (
                    <TableCell>
                      {this.props.canSortByUpdatedDate && (
                        <Tooltip title="Sort">
                          <TableSortLabel
                            active={
                              this.props.sort ===
                              SketchOrderSortParameter.UpdatedAt
                            }
                            onClick={this.sortByUpdatedDate}
                            direction={this.determineIfOrderIsAscOrDesc()}
                          >
                            Last Updated
                          </TableSortLabel>
                        </Tooltip>
                      )}
                      {!this.props.canSortByUpdatedDate && <div>Update</div>}
                    </TableCell>
                  )}
                  {this.props.showDueDate && (
                    <TableCell>
                      {this.props.canSortByDueDate && (
                        <Tooltip title="Sort">
                          <TableSortLabel
                            active={
                              this.props.sort ===
                              SketchOrderSortParameter.DueDate
                            }
                            onClick={this.sortByDueDate}
                            direction={this.determineIfOrderIsAscOrDesc()}
                          >
                            Due Date
                          </TableSortLabel>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                  {this.props.showTimeSinceRequested && (
                    <TableCell>
                      {
                        <Tooltip title="Sort">
                          <TableSortLabel
                            active={
                              this.props.sort ===
                              SketchOrderSortParameter.TimeSinceRequested
                            }
                            onClick={this.sortByTimeSinceRequested}
                            direction={this.determineIfOrderIsAscOrDesc()}
                          >
                            Time Since Requested
                          </TableSortLabel>
                        </Tooltip>
                      }
                    </TableCell>
                  )}
                  {this.props.showOrganizationName && (
                    <TableCell>Organization</TableCell>
                  )}
                  {this.props.showSketchTech && (
                    <TableCell>Sketch Tech</TableCell>
                  )}
                  {this.props.showRequester && <TableCell>Requester</TableCell>}
                  {this.props.showAddress && <TableCell>Address</TableCell>}
                  {this.props.showProjectName && <TableCell>Name</TableCell>}
                  {this.props.showCompletedDate && (
                    <TableCell>Completed</TableCell>
                  )}
                  {this.props.showReviewer && <TableCell>Reviewer</TableCell>}
                  {this.props.showCost && <TableCell>Cost</TableCell>}
                  <TableCell>#</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {this.props.sketchOrderIds.map(this.renderSketchOrder)}
              </TableBody>
            </Table>
          </div>
          <Toolbar>
            <div className={this.props.classes.flex} />
            <Pager
              currentPage={this.props.currentPage}
              totalPages={this.props.totalPages}
              nextPageClick={this.props.onNextPageClick}
              prevPageClick={this.props.onPrevPageClick}
            />
          </Toolbar>
        </Paper>

        <ChoseSketchOrdersFilterDialog
          open={this.state.chooseFilterDialogOpen}
          onDismissClick={this.closeFilterSketchOrderDialog}
          chooseStatusFilter={this.openStatusFilterDialog}
          numberOfStatusFilters={this.props.numberOfStatusFilters}
          numberOfOrganizationFilters={this.props.numberOfOrganizationFilters}
          numberOfRequesterFilters={this.props.numberOfRequesterFilters}
          numberOfOwnerFilters={this.props.numberOfOwnerFilters}
          canFilterByOrganization={this.props.canFilterByOrganization}
          canFilterByRequester={this.props.canFilterByRequester}
          canFilterByOwner={this.props.canFilterByOwner}
          chooseOrganizationFilter={this.openOrganizationFilterDialog}
          chooseRequesterFilter={this.openRequesterFilterDialog}
          chooseOwnerFilter={this.openOwnerFilterDialog}
        />

        <StatusSketchOrderFilterDialog
          open={this.state.statusFilterDialogOpen}
          onApplyClick={this.applyStatusFilters}
          onDismissClick={this.closeStatusFilterDialog}
        />

        <OrganizationSketchOrderFilterDialog
          open={this.state.organizationFilterDialogOpen}
          onApplyClick={this.applyOrganizationFilter}
          onDismissClick={this.closeOrganizationFilterDialog}
        />

        <RequesterSketchOrderFilterDialog
          open={this.state.requesterFilterDialogOpen}
          onApplyClick={this.applyRequesterFilter}
          onDismissClick={this.closeRequesterFilterDialog}
        />

        <OwnerSketchOrderFilterDialog
          open={this.state.ownerFilterDialogOpen}
          onApplyClick={this.applyOwnerFilter}
          onDismissClick={this.closeOwnerFilterDialog}
        />
      </div>
    );
  }
}

SketchOrderTable.propTypes = {
  subtitleIcon: PropTypes.node,
  subtitle: PropTypes.string,
  showProjectName: PropTypes.bool,
  showAddress: PropTypes.bool,
  showOrganizationName: PropTypes.bool,
  showCompletedDate: PropTypes.bool,
  showRequester: PropTypes.bool,
  showSketchTech: PropTypes.bool,
  showReviewer: PropTypes.bool,
  showStatus: PropTypes.bool,
  showCost: PropTypes.bool,
  showDueDate: PropTypes.bool,
  showLinksToSupportDashboard: PropTypes.bool,
  showRequestedAtTime: PropTypes.bool,
  showLastUpdatedAtTime: PropTypes.bool,
  showTimeSinceRequested: PropTypes.bool,
  canFilter: PropTypes.bool,
  numberOfStatusFilters: PropTypes.number,
  numberOfOrganizationFilters: PropTypes.number,
  numberOfRequesterFilters: PropTypes.number,
  numberOfOwnerFilters: PropTypes.number,
  canFilterByOrganization: PropTypes.bool,
  canFilterByRequester: PropTypes.bool,
  canFilterByOwner: PropTypes.bool,
  canSortByRequestedDate: PropTypes.bool,
  canSortByUpdatedDate: PropTypes.bool,
  canSortByDueDate: PropTypes.bool,
  sketchOrders: PropTypes.shape({
    sketchOrder: PropTypes.shape({
      id: PropTypes.number.isRequired,
      projectName: PropTypes.string,
      completedDate: PropTypes.string,
      requestedDate: PropTypes.string,
      updatedAt: PropTypes.string,
      address: PropTypes.string,
      city: PropTypes.string,
      region: PropTypes.string,
      postCode: PropTypes.string,
      country: PropTypes.string,
      sketchOrderStatus: PropTypes.string,
      cost: PropTypes.number,
      organization: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      owner: PropTypes.shape({
        userId: PropTypes.number,
        userName: PropTypes.string,
      }),
      requester: PropTypes.shape({
        userId: PropTypes.number,
        userName: PropTypes.string,
      }),
      projectId: PropTypes.string,
      sketchTech: PropTypes.shape({
        userName: PropTypes.string,
      }),
    }),
  }).isRequired,
  sketchOrderIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  totalOrders: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onNextPageClick: PropTypes.func.isRequired,
  onPrevPageClick: PropTypes.func.isRequired,
  classes: PropTypes.shape({
    flex: PropTypes.string.isRequired,
    subtitleIcon: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    tableWrapper: PropTypes.string.isRequired,
    margin: PropTypes.string.isRequired,
  }).isRequired,
  isFetching: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  onSortClick: PropTypes.func,
  sort: PropTypes.string,
  order: PropTypes.string,
  currentUserName: PropTypes.string.isRequired,
  filterStormClick: PropTypes.func,
  filterStorm: PropTypes.bool,
  showStormFilter: PropTypes.bool,
};

SketchOrderTable.defaultProps = {
  showProjectName: false,
  showAddress: false,
  showOrganizationName: false,
  showCompletedDate: false,
  showReviewer: false,
  showRequester: false,
  showSketchTech: false,
  showStatus: false,
  showCost: false,
  showDueDate: false,
  showLinksToSupportDashboard: false,
  showCreatedAtTime: false,
  showLastUpdatedAtTime: false,
  showTimeSinceRequested: false,
  canFilter: false,
  numberOfStatusFilters: 0,
  numberOfOrganizationFilters: 0,
  numberOfOwnerFilters: 0,
  canFilterByOrganization: false,
  canFilterByRequester: false,
  canFilterByOwner: false,
  isFetching: false,
  filterStorm: false,
  showStormFilter: false,
};

export default withRouter(withStyles(style)(SketchOrderTable));
